import React, { useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { List } from 'immutable';
import { isNil } from 'lodash';
import PropTypes from 'prop-types';

import Notes from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/ManagerNoteSection/Notes';
import Issue from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/TimecardSection/Issues/issue';
import { duration } from 'features/timesheets/util';

import { cxHelpers } from 'util/className';
import { moment } from 'util/dateTime';
import { toI18n } from 'util/i18n';

import Section from '../../Section';

import BreakPenaltyInput from './BreakPenaltyInput';
import MissingMandatedBreaks from './MissingMandatedBreaks';
import TimebreakRows from './TimebreakRows';

const { cx, cxEl } = cxHelpers('TimebreakSection');

const BREAK_NOTE_EVENT_TYPES = Object.freeze([
  'missed_break',
  'break_start',
  'break_end',
]);

const TimebreakSection = ({
  addTimebreak,
  breakPenaltiesEnabled,
  breakPenalty,
  dateStr,
  isMobileScreenSize,
  mandatedBreaks,
  onChange,
  onChangeTimebreak,
  removeTimebreak,
  shiftEndAt,
  shiftStartAt,
  timebreaks,
  validationErrors,
  employeeName,
  shift,
  setChangesMade,
}) => {
  const breakNotes = (shift.get('notes') || List()).filter(note =>
    BREAK_NOTE_EVENT_TYPES.includes(note.get('event_type'))
  );
  const paidBreaks = useMemo(
    () =>
      timebreaks.filter(timebreak => timebreak.get('paid') === true).toList(),
    [timebreaks]
  );

  const unpaidBreaks = useMemo(
    () =>
      timebreaks
        .filter(
          timebreak =>
            timebreak.get('paid') === false ||
            isNil(timebreak.get('mandated_break_id'))
        )
        .toList(),
    [timebreaks]
  );

  const paidMandatedBreaks = useMemo(
    () =>
      mandatedBreaks
        .filter(mandatedBreak => mandatedBreak.get('paid') === true)
        .toList(),
    [mandatedBreaks]
  );

  const unpaidMandatedBreaks = useMemo(
    () =>
      mandatedBreaks
        .filter(mandatedBreak => mandatedBreak.get('paid') === false)
        .toList(),
    [mandatedBreaks]
  );

  const paidMissingBreaksIssue = useMemo(() => {
    const issues = shift.get('issues');
    if (!issues) return null;

    return issues
      .filter(issue => issue.get('type') === 'missing_paid_breaks')
      .first();
  }, [shift]);

  const unpaidMissingBreaksIssue = useMemo(() => {
    const issues = shift.get('issues');
    if (!issues) return null;

    return issues
      .filter(issue => issue.get('type') === 'missing_unpaid_breaks')
      .first();
  }, [shift]);

  function numberOfMinutes(timebreak) {
    const endAt = moment(timebreak.get('end_at'));
    const startAt = moment(timebreak.get('start_at'));
    if (!endAt.isValid() || !startAt.isValid()) {
      return 0;
    }
    return endAt.diff(startAt, 'minutes');
  }

  const combineBreakDurations = filteredTimebreaks => {
    const completedTimebreaks = filteredTimebreaks.filter(
      timebreak =>
        timebreak.get('start_at') &&
        timebreak.get('end_at') &&
        !timebreak.get('remove')
    );
    return duration(
      completedTimebreaks.reduce((total, n) => total + numberOfMinutes(n), 0)
    );
  };

  const totalBreaks = () => {
    const completedTimebreaks = timebreaks.filter(
      timebreak =>
        timebreak.get('start_at') &&
        timebreak.get('end_at') &&
        !timebreak.get('remove')
    );
    return completedTimebreaks.count();
  };

  return (
    <Box className={cx()}>
      {mandatedBreaks.isEmpty() ? (
        <MissingMandatedBreaks />
      ) : (
        <Box>
          <Section
            className={cxEl('breaks')}
            title={toI18n('timecard_modal.timebreak_edit.section_header', {
              props: { count: totalBreaks() },
            })}
            iconName="Break"
            collapsedContent={
              breakNotes.count() > 0 && (
                <Notes notes={breakNotes} type="break_notes" />
              )
            }
          />
          <Section
            className={cxEl('paid-breaks')}
            collapsedContent={
              <Box>
                <Box pb={8}>
                  <Text
                    variant="bodySmBoldTight"
                    color="mono700"
                    i18n="timecard_modal_v3.timebreaks_section.edit_paid"
                    i18nProps={{ time: combineBreakDurations(paidBreaks) }}
                  />
                </Box>
                {paidMissingBreaksIssue && (
                  <Box pb={8}>
                    <Issue issue={paidMissingBreaksIssue} />
                  </Box>
                )}
                <TimebreakRows
                  breaks={paidBreaks}
                  mandatedBreaks={paidMandatedBreaks}
                  paid
                  errors={validationErrors.paid}
                  addTimebreak={addTimebreak}
                  dateStr={dateStr}
                  isMobileScreenSize={isMobileScreenSize}
                  onChangeTimebreak={onChangeTimebreak}
                  removeTimebreak={removeTimebreak}
                  shiftEndAt={shiftEndAt}
                  shiftStartAt={shiftStartAt}
                  employeeName={employeeName}
                  setChangesMade={setChangesMade}
                />
              </Box>
            }
          />
          <Section
            bottomBorder={!breakPenaltiesEnabled}
            bottomPadding={!breakPenaltiesEnabled}
            className={classnames(cxEl('unpaid-breaks'), {
              withoutBreakPenalties: !breakPenaltiesEnabled,
            })}
            collapsedContent={
              <Box>
                <Box pb={8}>
                  <Text
                    variant="bodySmBoldTight"
                    color="mono700"
                    i18n="timecard_modal_v3.timebreaks_section.edit_unpaid"
                    i18nProps={{ time: combineBreakDurations(unpaidBreaks) }}
                  />
                </Box>
                {unpaidMissingBreaksIssue && (
                  <Box pb={8}>
                    <Issue issue={unpaidMissingBreaksIssue} />
                  </Box>
                )}
                <TimebreakRows
                  breaks={unpaidBreaks}
                  mandatedBreaks={unpaidMandatedBreaks}
                  paid={false}
                  errors={validationErrors.unpaid}
                  addTimebreak={addTimebreak}
                  dateStr={dateStr}
                  isMobileScreenSize={isMobileScreenSize}
                  onChangeTimebreak={onChangeTimebreak}
                  removeTimebreak={removeTimebreak}
                  shiftEndAt={shiftEndAt}
                  shiftStartAt={shiftStartAt}
                  employeeName={employeeName}
                  setChangesMade={setChangesMade}
                />
              </Box>
            }
          />
        </Box>
      )}
      {breakPenaltiesEnabled && (
        <BreakPenaltyInput
          onChange={onChange}
          breakPenalty={breakPenalty}
          setChangesMade={setChangesMade}
        />
      )}
    </Box>
  );
};

TimebreakSection.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  breakPenaltiesEnabled: PropTypes.bool,
  breakPenalty: PropTypes.number,
  dateStr: PropTypes.string,
  isMobileScreenSize: PropTypes.bool,
  mandatedBreaks: ImmutablePropTypes.list,
  onChange: PropTypes.func,
  onChangeTimebreak: PropTypes.func.isRequired,
  removeTimebreak: PropTypes.func.isRequired,
  shiftEndAt: PropTypes.object,
  shiftStartAt: PropTypes.object,
  timebreaks: ImmutablePropTypes.list,
  validationErrors: PropTypes.object,
  employeeName: PropTypes.string,
  shift: ImmutablePropTypes.map,
  setChangesMade: PropTypes.func.isRequired,
};

export default TimebreakSection;
